import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "row", "typeSelect", "fieldSelect", "modal", "hiddenData",
    "columnName", "columnNameEdit", "customFieldName",
    "catalogBtn", "inventoryBtn", "catalogDesc", "inventoryDesc",
    "catalogTips", "inventoryTips", "inventorySettings", "submitBtn"
  ]
  static values = { mode: String }

  connect() {
    // Parse standard and custom fields from hidden data
    const standardFieldsEl = this.hiddenDataTarget.querySelector('[data-standard-fields]')
    const customFieldsEl = this.hiddenDataTarget.querySelector('[data-custom-fields]')

    if (standardFieldsEl) {
      this.standardFields = JSON.parse(standardFieldsEl.dataset.standardFields)
    }

    if (customFieldsEl) {
      this.customFields = JSON.parse(customFieldsEl.dataset.customFields)
    }

    // Initialize mode if not set
    if (!this.modeValue) {
      this.modeValue = 'catalog'
    }

    // Initialize UI for current mode
    this.refreshModeUI()

    // Initialize field select visibility on page load
    this.initializeFieldVisibility()
  }

  switchMode(event) {
    const mode = event.params.mode || event.target.dataset.mode
    if (this.modeValue === mode) return

    this.modeValue = mode
    this.refreshModeUI()

    // Update all mapping type dropdowns
    this.typeSelectTargets.forEach(select => {
      const currentValue = select.value
      this.rebuildMappingOptionsForMode(select, mode)

      // Keep current value if still valid
      if (select.querySelector(`option[value="${currentValue}"]`)) {
        select.value = currentValue
      } else {
        select.value = ''
        // Trigger change to hide dependent fields
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  refreshModeUI() {
    const mode = this.modeValue

    // Update button styles
    if (this.hasCatalogBtnTarget && this.hasInventoryBtnTarget) {
      if (mode === 'catalog') {
        this.catalogBtnTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        this.catalogBtnTarget.style.color = 'white'
        this.catalogBtnTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
        this.inventoryBtnTarget.style.background = 'transparent'
        this.inventoryBtnTarget.style.color = 'var(--text-secondary)'
        this.inventoryBtnTarget.style.boxShadow = 'none'
      } else {
        this.inventoryBtnTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
        this.inventoryBtnTarget.style.color = 'white'
        this.inventoryBtnTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
        this.catalogBtnTarget.style.background = 'transparent'
        this.catalogBtnTarget.style.color = 'var(--text-secondary)'
        this.catalogBtnTarget.style.boxShadow = 'none'
      }
    }

    // Toggle descriptions
    if (this.hasCatalogDescTarget) this.catalogDescTarget.style.display = mode === 'catalog' ? '' : 'none'
    if (this.hasInventoryDescTarget) this.inventoryDescTarget.style.display = mode === 'inventory' ? '' : 'none'

    // Toggle tips
    if (this.hasCatalogTipsTarget) this.catalogTipsTarget.style.display = mode === 'catalog' ? '' : 'none'
    if (this.hasInventoryTipsTarget) this.inventoryTipsTarget.style.display = mode === 'inventory' ? '' : 'none'

    // Toggle inventory settings
    if (this.hasInventorySettingsTarget) this.inventorySettingsTarget.style.display = mode === 'inventory' ? '' : 'none'

    // Update submit button text
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.value = mode === 'inventory'
        ? this.submitBtnTarget.dataset.inventoryText
        : this.submitBtnTarget.dataset.catalogText
    }

    // Rebuild mapping types for all selects
    this.typeSelectTargets.forEach(select => {
      this.rebuildMappingOptionsForMode(select, mode)
    })
  }

  rebuildMappingOptionsForMode(select, mode) {
    const currentValue = select.value
    select.innerHTML = ''

    // Always add ignore
    const ignoreOption = document.createElement('option')
    ignoreOption.value = ''
    ignoreOption.textContent = '-- Ignore Column --'
    select.appendChild(ignoreOption)

    if (mode === 'catalog') {
      const productGroup = document.createElement('optgroup')
      productGroup.label = 'Product Fields'

      const options = [
        { value: 'standard', text: 'Standard Field' },
        { value: 'custom', text: 'Custom Field' },
        { value: 'variant_identifier', text: 'Variant Identifier' },
        { value: 'variant_attribute', text: 'Variant Attribute' },
        { value: 'image_url', text: 'Image URL' },
        { value: 'price_tier', text: 'Price Tier' }
      ]

      options.forEach(opt => {
        const option = document.createElement('option')
        option.value = opt.value
        option.textContent = opt.text
        if (currentValue === opt.value) option.selected = true
        productGroup.appendChild(option)
      })
      select.appendChild(productGroup)
    } else {
      const inventoryGroup = document.createElement('optgroup')
      inventoryGroup.label = 'Inventory Management'

      const options = [
        { value: 'classification', text: '📦 Classification (Product Name/Pattern)' },
        { value: 'stock_replace', text: '🔄 Stock Update (Replace)' },
        { value: 'stock_add', text: '➕ Stock Update (Add)' },
        { value: 'incoming_shipment', text: '🚢 Incoming Shipment' }
      ]

      options.forEach(opt => {
        const option = document.createElement('option')
        option.value = opt.value
        option.textContent = opt.text
        if (currentValue === opt.value) option.selected = true
        inventoryGroup.appendChild(option)
      })
      select.appendChild(inventoryGroup)

      const additionalGroup = document.createElement('optgroup')
      additionalGroup.label = 'Additional Fields'
      const addOptions = [
        { value: 'standard', text: 'Standard Field' },
        { value: 'custom', text: 'Custom Field' }
      ]
      addOptions.forEach(opt => {
        const option = document.createElement('option')
        option.value = opt.value
        option.textContent = opt.text
        if (currentValue === opt.value) option.selected = true
        additionalGroup.appendChild(option)
      })
      select.appendChild(additionalGroup)
    }
  }

  initializeFieldVisibility() {
    this.typeSelectTargets.forEach(typeSelect => {
      const column = typeSelect.dataset.column
      const targetType = typeSelect.value

      // Find the corresponding field select
      const fieldSelect = this.fieldSelectTargets.find(
        select => select.dataset.column === column
      )

      if (!fieldSelect) return

      // Show/hide field select based on initial type value
      if (['standard', 'custom', 'price_tier', 'variant_attribute'].includes(targetType)) {
        fieldSelect.style.display = ''
        // Also check if we need to show the custom field name input
        if (['custom', 'variant_attribute'].includes(targetType) && fieldSelect.value === '__create_new__') {
          const input = this.customFieldNameTargets.find(el => el.dataset.column === column)
          if (input) input.style.display = ''
        }
      } else {
        fieldSelect.style.display = 'none'
      }
    })
  }

  updateFieldOptions(event) {
    const typeSelect = event.target
    const column = typeSelect.dataset.column
    const targetType = typeSelect.value

    // Find the corresponding field select and custom field input
    const fieldSelect = this.fieldSelectTargets.find(
      select => select.dataset.column === column
    )
    const customFieldInput = this.customFieldNameTargets.find(
      input => input.dataset.column === column
    )

    if (!fieldSelect) return

    // Show/hide field select based on type
    if (['standard', 'custom', 'price_tier', 'variant_attribute'].includes(targetType)) {
      fieldSelect.style.display = ''
      this.populateFieldOptions(fieldSelect, targetType, column)

      // Reset custom field input visibility when type changes
      if (customFieldInput) customFieldInput.style.display = 'none'
    } else {
      fieldSelect.style.display = 'none'
      fieldSelect.value = ''
      if (customFieldInput) customFieldInput.style.display = 'none'
    }
  }

  toggleNewCustomFieldInput(event) {
    const fieldSelect = event.target
    const column = fieldSelect.dataset.column
    const customFieldInput = this.customFieldNameTargets.find(
      input => input.dataset.column === column
    )

    if (customFieldInput) {
      if (fieldSelect.value === '__create_new__') {
        customFieldInput.style.display = ''
        customFieldInput.focus()
      } else {
        customFieldInput.style.display = 'none'
      }
    }
  }

  populateFieldOptions(fieldSelect, targetType, column) {
    // Clear existing options
    fieldSelect.innerHTML = ''

    if (targetType === 'standard') {
      // Add standard field options
      const optgroup = document.createElement('optgroup')
      optgroup.label = 'Standard Fields'

      this.standardFields.forEach(([label, value]) => {
        const option = document.createElement('option')
        option.value = value
        option.textContent = label
        optgroup.appendChild(option)
      })

      fieldSelect.appendChild(optgroup)

    } else if (targetType === 'custom') {
      // Add custom field options
      const optgroup = document.createElement('optgroup')
      optgroup.label = 'Custom Fields'

      this.customFields.forEach(field => {
        const option = document.createElement('option')
        option.value = field.name
        option.textContent = field.label
        optgroup.appendChild(option)
      })

      // Add "Create New" option
      const createOption = document.createElement('option')
      createOption.value = '__create_new__'
      createOption.textContent = 'Create New...'
      optgroup.appendChild(createOption)

      fieldSelect.appendChild(optgroup)

    } else if (targetType === 'variant_attribute') {
      // currentValue is bound per-method (switchMode / rebuildMappingOptionsForMode
      // have their own); populateFieldOptions did not, so this branch threw
      // ReferenceError the moment a user picked "Variant Attribute", leaving the
      // Target Field select empty and the mapping type unusable.
      const currentValue = fieldSelect.value

      // Add variant attribute options
      const optgroup = document.createElement('optgroup')
      optgroup.label = 'Common Attributes'

      const commonAttrs = [
        ['Color', 'color'],
        ['Size', 'size'],
        ['Material', 'material'],
        ['Style', 'style'],
        ['Metal Type', 'metal_type'],
        ['Finish', 'finish']
      ]

      let matched = false
      commonAttrs.forEach(([label, value]) => {
        const option = document.createElement('option')
        option.value = value
        option.textContent = label
        if (currentValue === value) {
          option.selected = true
          matched = true
        }
        optgroup.appendChild(option)
      })

      // Add "Create New" option
      const createOption = document.createElement('option')
      createOption.value = '__create_new__'
      createOption.textContent = 'Create New Variant Property...'
      if (!matched && currentValue) {
        createOption.selected = true
      }
      optgroup.appendChild(createOption)

      fieldSelect.appendChild(optgroup)

      // Show/hide custom name input for variant attribute
      const input = this.customFieldNameTargets.find(el => el.dataset.column === column)
      if (input) {
        if (!matched && currentValue) {
          input.style.display = ''
          if (!input.value) input.value = currentValue
        } else if (fieldSelect.value === '__create_new__') {
          input.style.display = ''
        }
      }

    } else if (targetType === 'price_tier') {
      // Add price tier options
      const tiers = [
        ['Retail', 'Retail'],
        ['Wholesale', 'Wholesale'],
        ['Distributor', 'Distributor']
      ]

      tiers.forEach(([label, value]) => {
        const option = document.createElement('option')
        option.value = value
        option.textContent = label
        fieldSelect.appendChild(option)
      })

      // Add custom option
      const customOption = document.createElement('option')
      customOption.value = column
      customOption.textContent = `Custom: ${column}`
      fieldSelect.appendChild(customOption)
    }
  }

  openSaveTemplateModal(event) {
    event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.style.display = 'block'
      // Focus on the template name input
      const input = this.modalTarget.querySelector('input[name="template_name"]')
      if (input) {
        setTimeout(() => input.focus(), 100)
      }
    }
  }

  closeSaveTemplateModal(event) {
    event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.style.display = 'none'
    }
  }

  editColumnName(event) {
    event.preventDefault()
    const button = event.currentTarget
    const column = button.dataset.column

    // Find the column name display and edit elements for this column
    const nameDisplay = this.columnNameTargets.find(el => el.dataset.column === column)
    const nameEdit = this.columnNameEditTargets.find(el => el.dataset.column === column)

    if (nameDisplay && nameEdit) {
      nameDisplay.style.display = 'none'
      nameEdit.style.display = ''

      // Focus on the input
      const input = nameEdit.querySelector('input')
      if (input) {
        input.focus()
        input.select()
      }
    }
  }

  saveColumnName(event) {
    event.preventDefault()
    const button = event.currentTarget
    const column = button.dataset.column

    const nameDisplay = this.columnNameTargets.find(el => el.dataset.column === column)
    const nameEdit = this.columnNameEditTargets.find(el => el.dataset.column === column)
    const input = nameEdit.querySelector('input')

    if (nameDisplay && nameEdit && input) {
      const newName = input.value.trim()
      if (newName) {
        // Update the display text
        const displayText = nameDisplay.querySelector('[data-display-text]')
        if (displayText) {
          displayText.textContent = newName
        }

        // Hide edit, show display
        nameEdit.style.display = 'none'
        nameDisplay.style.display = ''
      }
    }
  }

  cancelEditColumnName(event) {
    event.preventDefault()
    const button = event.currentTarget
    const column = button.dataset.column

    const nameDisplay = this.columnNameTargets.find(el => el.dataset.column === column)
    const nameEdit = this.columnNameEditTargets.find(el => el.dataset.column === column)
    const input = nameEdit.querySelector('input')

    if (nameDisplay && nameEdit && input) {
      // Reset input to original value
      const displayText = nameDisplay.querySelector('[data-display-text]')
      if (displayText) {
        input.value = displayText.textContent
      }

      // Hide edit, show display
      nameEdit.style.display = 'none'
      nameDisplay.style.display = ''
    }
  }

  // Utility function to humanize column names
  humanize(str) {
    return str
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }
}
